import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom'
import { useNavigate } from 'react-router';
import 'devextreme/data/odata/store';
import FileUploader from 'devextreme-react/file-uploader';
import devices from 'devextreme/core/devices';
import notify from 'devextreme/ui/notify';

import DataGrid, {
  Column,
  Pager,
  Paging,
  FilterRow,
  SearchPanel,
  Editing,
  Button as DGButton,
  Lookup,
} from 'devextreme-react/data-grid';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import Button from 'devextreme-react/button';
import { TagBox } from 'devextreme-react/tag-box';
import { SelectBox } from 'devextreme-react/select-box';
import {
  Form,
  Label,
  SimpleItem,
  EmptyItem,
  GroupItem,
  ButtonItem,
  RequiredRule,
} from 'devextreme-react/form';
import { useAuth } from '../../contexts/auth';
import { adminGetCompanies, adminUpdateCompany } from '../../api/api'
import { uploadFileChunk } from '../../api/s3'
import { Popup, Position, ToolbarItem } from 'devextreme-react/popup';

const isPhone = devices.current().deviceType === 'phone'

export default function ManageCompanies() {
  const { user, refreshSession } = useAuth();
  const [companiesList, setCompaniesList] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [loading, setLoading] = useState(true)
  const fileExtensions = ['.jpg', '.jpeg', '.png'];
  const dataGridRef = useRef(null);
  const formRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const permissionsList = [{label:"Admin", value:"admin"},{label:"Company Admin", value:"company_admin"},{label:"Company Wide Read/Write", value:"company_rw"},{label:"Company Wide Read Only", value:"company_ro"},{label:"Company Member", value:"company_member"}]
  const [sessionToken, setSessionToken] = useState();


  useEffect(() => {
    if (user.signInUserSession) {
      adminGetCompanies(user, onGetCompaniesSuccess, onError);
    }
  }, [user && user.signInUserSession, user.signInUserSession.idToken.payload]);

  useEffect(() => {
    if (currentCompany) {
      setCancelButtonOptions({
        disabled: false,
        text: 'Cancel',
        width: '100%',
        onClick: onCancel,
      });
      setSaveButtonOptions({
        disabled: !(currentCompany.company_name),
        text: 'Save',
        type: 'default',
        width: '100%',
        onClick: onSave,
      });
    }
  }, [currentCompany]);


  const onCancel = () => {
    setCurrentCompany(null);
  }

  const onSave = async () => {
    setSaveButtonOptions({
      disabled: true,
      text: 'Save',
      type: 'default',
      width: '100%',
      onClick: onSave,
    });
    adminUpdateCompany(user, currentCompany, onSaveSuccess, onError);
  }

  const onError = useCallback((error) => {
    console.log('error occured')
    if (error.status === 401) {
      refreshSession();
    } else {
      notify("An error has occured. Please try again later.", 'error', 2000);
      console.log(error);
    }
  }, [refreshSession]);

  const [cancelButtonOptions, setCancelButtonOptions] = useState({
    disabled: false,
    text: 'Cancel',
    width: '100%',
    onClick: onCancel,
  });

  const [saveButtonOptions, setSaveButtonOptions] = useState({
    disabled: false,
    text: 'Save',
    type: 'default',
    width: '100%',
    onClick: onSave,
  });

  const onSaveSuccess = useCallback((data) => {
    setCurrentCompany(null);
    adminGetCompanies(user, onGetCompaniesSuccess, onError);
  }, [])

  const onGetCompaniesSuccess = useCallback((data) => {
    data = data.sort((a,b) => (a.company_name > b.company_name) ? 1 : ((b.company_name > a.company_name) ? -1 : 0))
    setCompaniesList(data);
    setLoading(false);
  }, [])

  const removeRow = e => {
    //deleteResource(user, e.key, onSaveSuccess, onError)
  }

  const phoneEditorOptions = { mask: '(000) 000-0000' };

  const onAdd = useCallback( () => {
    setCurrentCompany(
      {
      });
  }, [])

  const onFormDataChanged = (e) => {
    setSaveButtonOptions({
      disabled: !(currentCompany.company_name),
      text: 'Save',
      type: 'default',
      width: '100%',
      onClick: onSave,
    });
  }

  const cellRender = (data) => {
    return (
      <div
        style={{
          backgroundImage: `url("${data.data.picture?data.data.picture:'/DefaultAvatar.png'}")`,
          backgroundSize: 'cover',
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          width: '100%',
          aspectRatio: 1,
          borderRadius: '50%'
        }}
      />

    );
  }


  if(!loading) {
    return (
      <React.Fragment>
        <div className="d-flex flex-row justify-content-between align-items-center content-block">
          <h2 className={''}>Manage Companies</h2>
          <Button
            type="default"
            text="Add Company"
            onClick={onAdd}
          />
        </div>

        <DataGrid
          ref={dataGridRef}
          className={'dx-card wide-card'}
          dataSource={companiesList}
          showBorders={false}
          focusedRowEnabled={false}
          defaultFocusedRowIndex={0}
          columnAutoWidth={true}
          columnHidingEnabled={true}
          onRowRemoved={removeRow}
        >

          <Editing
            mode="row"
            allowUpdating={true}
            allowDeleting={true}
            // allowAdding={true}
          />
          <Editing
            mode="row"
            allowUpdating={true}
            allowDeleting={true}
          />

          <Paging defaultPageSize={50} />
          <Pager showPageSizeSelector={true} showInfo={true} />
          <SearchPanel
            visible={true}
            width="250"
            placeholder="Search..."
          />

          <Column
            dataField={'company_name'}
            caption={'Company Name'}
            hidingPriority={0}
          >
            <RequiredRule />
          </Column>

          <Column type="buttons">
            <DGButton
                text="Edit"
                icon="fas fa-pen fs-6"
                hint="Edit"
                onClick={(e) => {
                  setCurrentCompany(e.row.data);
                }}
            />
            <DGButton name="delete" />
          </Column>

          <Editing
            allowEditing={true}
            mode="popup"
            allowUpdating={true}
            // allowDeleting={true}
            // allowAdding={true}
          />
        </DataGrid>

        <Popup
          className="text-danger"
          visible={currentCompany !== null}
          onHidden={() => setCurrentCompany(null)}
          dragEnabled={false}
          showCloseButton={false}
          showTitle={true}
          fullScreen={isPhone}
          title={currentCompany?.company_id?"Edit Company":"Add Company"}
          container=".dx-viewport"
          width={380}
          height={720}
        >
            <Position at="center" my="center"collision="fit" />
            <ToolbarItem
              widget="dxButton"
              toolbar="bottom"
              location="before"
              options={saveButtonOptions}
            />
            <ToolbarItem
              widget="dxButton"
              toolbar="bottom"
              location="after"
              options={cancelButtonOptions}
            />


            <Form
              ref={formRef}
              id={'form'}
              formData={currentCompany}
              labelLocation={'top'}
              onFieldDataChanged={onFormDataChanged}
            >
                <SimpleItem
                  dataField="company_name"
                >
                  <Label text="Company Name"/>
                  <RequiredRule message="Name is required" />
                </SimpleItem>

            </Form>

        </Popup>

      </React.Fragment>
    )
  } else {
    return (
      <div className="d-flex flex-row justify-content-center align-items-center mt-5 pt-5">
          <LoadIndicator
              id="large-indicator"
              height={60}
              width={60}
          />
      </div>
    )
  }
}
